import { useEffect, useState, useRef, Suspense, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  useGetCategoryFrontQuery,
  useLazyGetProductFrontQuery,
} from "@/store/api/branchApi";
import type { RootState } from "@/store";
import { checkOperatingHours } from "@/utils/hooks/timeUtils";
import Spinner from "../loader/Spinner";
import ProductCard from "./ProductCard";
import ProductCardSkeleton from "./ProductCardSkeleton";
import { Product } from "@/types/product";
import SearchIcon from "@/assets/img/SearchIcon";
import CloseIcon from "@/assets/img/CloseIcon";
import { searchProductsByBranchAndMenu } from "@/store/api/searchApi";
import CategorySkeleton from "./CategorySkeleton";
import { useWindowSize } from "@/utils/hooks/useWindowSize";
import { updateHasServiceInCart } from "@/store/features/cartSlice";
import CategoryNavigation from "./CategoryNavigation";
import DailySpecialSection from "./DailySpecialSection";

interface Category {
  id: number;
  name: string;
  ranking?: number;
  category_hours?: any[];
  status?: string;
}

interface MenuProps {
  branch: {
    id: string;
    timezone: string;
    name: string;
    menu_preference?: "Physical Products" | "Services";
  };
  setMenuResourcesLoaded: (loaded: boolean) => void;
}

// Define loading states for better tracking
type CategoryLoadingState = "not_loaded" | "loading" | "loaded";

const Menu = ({ branch, setMenuResourcesLoaded }: MenuProps) => {
  const { height: windowHeight } = useWindowSize();
  const [productType, setProductType] = useState<string>("");
  const dispatch = useDispatch();
  const [productsLoaded, setProductsLoaded] = useState<boolean>(false);

  const [sortedOpenCategories, setSortedOpenCategories] = useState<Category[]>(
    [],
  );
  // Store all products by category regardless of product type
  const [allProductsByCategory, setAllProductsByCategory] = useState<
    Record<string, Product[]>
  >({});
  // Filtered products based on selected product type
  const [categoryProductsMap, setCategoryProductsMap] = useState<
    Record<string, Product[]>
  >({});
  // Products further filtered by search, derived from categoryProductsMap
  const [filteredCategoryProductsMap, setFilteredCategoryProductsMap] =
    useState<Record<string, Product[]>>({});

  // Improved state for tracking category loading states
  const [categoryLoadingStates, setCategoryLoadingStates] = useState<
    Record<string, CategoryLoadingState>
  >({});

  // Track if all categories are loaded to avoid redundant fetches
  const [allCategoriesLoaded, setAllCategoriesLoaded] =
    useState<boolean>(false);

  // For normal search usage
  const [searchText, setSearchText] = useState("");

  // State to handle modal overlay
  const [searchModalOpen, setSearchModalOpen] = useState(false);

  // Automatically trigger keyboard for mobile screens
  const mobileSearchInputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (searchModalOpen && /Mobi|Android/i.test(navigator.userAgent)) {
      mobileSearchInputRef.current?.focus();
    }
  }, [searchModalOpen]);

  // Track active category
  const [activeCategory, setActiveCategory] = useState<string>("");

  // Track if we're currently scrolling to a category (to prevent interim highlights)
  const [isScrollingToCategory, setIsScrollingToCategory] = useState(false);

  const storeStatus = useSelector((state: RootState) => state.poll.storeStatus);

  // FETCH CATEGORIES
  const { data: categories, isLoading: isCategoriesLoading } =
    useGetCategoryFrontQuery(
      {
        branch: branch?.id || "",
        branchTimezone: branch?.timezone || "America/Chicago",
      },
      {
        skip: !branch?.id,
        refetchOnMountOrArgChange: true,
      },
    );

  // LAZY FETCH PRODUCTS
  const [fetchProducts] = useLazyGetProductFrontQuery();

  // Create a ref for the loading observer
  const loadingObserver = useRef<IntersectionObserver | null>(null);

  const [showProductTypeSwitches, setShowProductTypeSwitches] = useState(false);

  // Ref to track if background loading has started for the current product type
  const backgroundLoadingStartedRef = useRef<boolean>(false);

  useEffect(() => {
    const fetchAllProducts = async () => {
      if (!branch?.id || !sortedOpenCategories.length) return;

      const { data: allProducts } = await fetchProducts({
        branch: branch.id,
        search: "",
      });

      let countPhysicalProducts = 0;
      let countServices = 0;

      allProducts?.forEach((product) => {
        if (product.product_type === "Physical Product") {
          countPhysicalProducts++;
        } else if (product.product_type === "Service") {
          countServices++;
        }
      });

      if (countPhysicalProducts > 0 && countServices > 0) {
        setShowProductTypeSwitches(true);
      } else {
        setShowProductTypeSwitches(false);
        setProductType("");
      }
    };

    fetchAllProducts();
  }, [branch?.id, sortedOpenCategories]);

  useEffect(() => {
    dispatch(updateHasServiceInCart());
  }, [dispatch]);

  // Once categories are loaded, sort them and filter out closed categories
  useEffect(() => {
    if (categories && branch?.timezone) {
      const sorted = [...categories].sort(
        (a, b) => (a.ranking ?? 0) - (b.ranking ?? 0),
      );

      // Filter out closed categories based on store/category hours
      const open = sorted.filter(
        (cat) =>
          !cat.category_hours?.length ||
          (checkOperatingHours(cat.category_hours, branch.timezone) &&
            storeStatus.isOpen),
      );

      setSortedOpenCategories(open);

      // Initialize all categories as "not_loaded"
      const initialLoadingStates: Record<string, CategoryLoadingState> = {};
      open.forEach((cat) => {
        initialLoadingStates[cat.name] = "not_loaded";
      });
      setCategoryLoadingStates(initialLoadingStates);
    }
  }, [categories, storeStatus.isOpen, branch?.timezone]);

  // Improved loading observer with better thresholds
  useEffect(() => {
    if (loadingObserver.current) loadingObserver.current.disconnect();

    loadingObserver.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const catName = entry.target.getAttribute("data-category");
            if (catName && categoryLoadingStates[catName] === "not_loaded") {
              queueMicrotask(() => {
                setCategoryLoadingStates((prev) => ({
                  ...prev,
                  [catName]: "loading",
                }));
                fetchCategoryProducts(catName);
              });
            }
          }
        });
      },
      {
        // Start loading when category is 1500px away for better pre-loading
        rootMargin: "1500px 0px",
        threshold: [0, 0.1, 0.5],
      },
    );

    // Attach observer to category divs
    Object.entries(categoryRefs.current).forEach(([catName, ref]) => {
      if (
        ref &&
        loadingObserver.current &&
        categoryLoadingStates[catName] === "not_loaded"
      ) {
        loadingObserver.current.observe(ref);
      }
    });

    return () => {
      if (loadingObserver.current) loadingObserver.current.disconnect();
    };
  }, [categoryLoadingStates, sortedOpenCategories]);

  // Set product type based on menu_preference when branch changes
  useEffect(() => {
    if (branch.menu_preference) {
      // Make sure we convert menu_preference to the correct API parameter format
      if (branch.menu_preference === "Physical Products") {
        setProductType("Physical Product");
      } else if (branch.menu_preference === "Services") {
        setProductType("Service");
      } else {
        // If it's already in the correct format, use it directly
        setProductType(branch.menu_preference);
      }
    } else {
      // Default to Physical Product if no preference is set
      setProductType("Physical Product");
    }
  }, [branch.menu_preference]);

  // Function to fetch products for a specific category - now fetches ALL products regardless of product type
  const fetchCategoryProducts = useCallback(
    async (catName: string) => {
      if (!branch?.id) return;

      // IMPORTANT: Add safeguard to prevent re-fetching already loaded categories
      // Get latest state to check if already loaded or loading
      let currentLoadingState;
      setCategoryLoadingStates((prev) => {
        currentLoadingState = prev[catName];
        // If already loaded or loading, don't fetch again
        if (currentLoadingState === "loaded") {
          return prev;
        }

        // Otherwise update to loading and continue
        return {
          ...prev,
          [catName]: "loading",
        };
      });

      // Exit early if already loaded (determined from closure value)
      if (currentLoadingState === "loaded") {
        console.log(`Skip fetching: Category ${catName} already loaded`);
        return;
      }

      try {
        console.log(`API FETCH for category: ${catName}`);
        // Fetch all products for this category without product type filter
        const { data } = await fetchProducts({
          branch: branch.id,
          search: "",
          category: catName,
          // No product_type filter - we get ALL products and filter client-side
        });

        console.log(`Category ${catName}: Loaded ${data?.length} products`);

        // Store ALL products in the allProductsByCategory map
        setAllProductsByCategory((prev) => ({
          ...prev,
          [catName]: data || [],
        }));

        // Mark as loaded once data is available
        setCategoryLoadingStates((prev) => ({
          ...prev,
          [catName]: "loaded",
        }));
      } catch (error) {
        console.error(
          `Error fetching products for category ${catName}:`,
          error,
        );

        // Even on error, mark as "loaded" to prevent infinite retries
        setCategoryLoadingStates((prev) => ({
          ...prev,
          [catName]: "loaded",
        }));
      }
    },
    [branch?.id, fetchProducts],
  );

  // Apply product type filter without re-fetching
  useEffect(() => {
    // Skip if no products cached yet
    if (Object.keys(allProductsByCategory).length === 0) return;

    console.log(
      `CLIENT-SIDE FILTER: Applying product type filter: ${productType}`,
    );

    // Filter cached products when product type changes
    const newCategoryProductsMap: Record<string, Product[]> = {};

    Object.entries(allProductsByCategory).forEach(([catName, products]) => {
      // Filter products by product type if one is selected
      const filteredProducts = productType
        ? products.filter((product) => product.product_type === productType)
        : products;

      newCategoryProductsMap[catName] = filteredProducts;
    });

    setCategoryProductsMap(newCategoryProductsMap);
    // Also update filtered map to maintain the current search results
    setFilteredCategoryProductsMap(newCategoryProductsMap);
    console.log("Finished applying product type filter");
  }, [productType, allProductsByCategory]);

  // Fetch products for initial visible categories and then load all in background
  useEffect(() => {
    const fetchCategoriesData = async () => {
      if (!branch?.id || !sortedOpenCategories.length) return;

      // Reset loading flag at the beginning of a new fetch cycle
      setAllCategoriesLoaded(false);
      // Reset background loading tracking for new product type
      backgroundLoadingStartedRef.current = false;

      try {
        // Step 1: Load first 3 categories immediately for fast initial render
        const initialCategories = sortedOpenCategories.slice(0, 3);
        const newLoadingStates = { ...categoryLoadingStates };

        initialCategories.forEach((cat) => {
          newLoadingStates[cat.name] = "loading";
        });
        setCategoryLoadingStates(newLoadingStates);

        // Load first 3 categories in parallel
        await Promise.all(
          initialCategories.map(async (cat) => {
            await fetchCategoryProducts(cat.name);
          }),
        );

        // Set initial active category
        if (!activeCategory && sortedOpenCategories.length > 0) {
          setActiveCategory(sortedOpenCategories[0].name);
        }

        // Mark initial loading as complete
        setProductsLoaded(true);

        // Step 2: Load all remaining categories in the background
        if (!allCategoriesLoaded && !backgroundLoadingStartedRef.current) {
          // Mark that background loading has started for this product type
          backgroundLoadingStartedRef.current = true;

          // Set a small delay to prioritize UI responsiveness after initial render
          setTimeout(async () => {
            // Get remaining categories that haven't been loaded yet
            const remainingCategories = sortedOpenCategories
              .filter((cat) => !initialCategories.includes(cat))
              .filter((cat) => categoryLoadingStates[cat.name] !== "loaded");

            if (remainingCategories.length > 0) {
              // Mark all as loading
              setCategoryLoadingStates((prev) => {
                const updatedStates = { ...prev };
                remainingCategories.forEach((cat) => {
                  updatedStates[cat.name] = "loading";
                });
                return updatedStates;
              });

              // Load all remaining categories in smaller batches to avoid overwhelming the API
              const batchSize = 5;
              for (let i = 0; i < remainingCategories.length; i += batchSize) {
                const batch = remainingCategories.slice(i, i + batchSize);
                await Promise.all(
                  batch.map(async (cat) => {
                    await fetchCategoryProducts(cat.name);
                  }),
                );
              }

              // Mark that all categories are now loaded
              setAllCategoriesLoaded(true);
              console.log("All category data loaded and cached");
            } else {
              // If no remaining categories, all are already loaded
              setAllCategoriesLoaded(true);
            }
          }, 500); // Small delay to ensure UI is responsive first
        }
      } catch (error) {
        console.error("Error fetching category products:", error);
      }
    };

    // When branch or categories change, we re-fetch everything
    if (branch?.id && sortedOpenCategories.length > 0) {
      // Reset all state
      setAllProductsByCategory({});
      setCategoryProductsMap({});
      setFilteredCategoryProductsMap({});

      // Reset all categories to not_loaded
      const resetLoadingStates: Record<string, CategoryLoadingState> = {};
      sortedOpenCategories.forEach((cat) => {
        resetLoadingStates[cat.name] = "not_loaded";
      });
      setCategoryLoadingStates(resetLoadingStates);

      fetchCategoriesData();
    }
  }, [branch?.id, sortedOpenCategories, fetchCategoryProducts]);

  // When product type changes, disconnect observers since we're loading everything up front
  useEffect(() => {
    if (allCategoriesLoaded && loadingObserver.current) {
      loadingObserver.current.disconnect();
    }
  }, [allCategoriesLoaded]);

  // Debounced text search: filter products client-side
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (!searchText.trim()) {
        // No search text, just show products filtered by product type
        setFilteredCategoryProductsMap(categoryProductsMap);
        return;
      }

      try {
        // Client-side search filtering
        const searchLower = searchText.toLowerCase();
        const filteredMap: Record<string, Product[]> = {};

        // Filter the already product-type-filtered map (categoryProductsMap)
        Object.entries(categoryProductsMap).forEach(([catName, products]) => {
          filteredMap[catName] = products.filter((product) =>
            product.menu_display_name?.toLowerCase().includes(searchLower),
          );
        });

        setFilteredCategoryProductsMap(filteredMap);
      } catch (error) {
        console.error("Error performing search:", error);
      }
    }, 300); // Reduced debounce time since it's just client-side filtering now

    return () => clearTimeout(debounceTimer);
  }, [searchText, categoryProductsMap]);

  // Refs to category sections (for scroll-spy / IntersectionObserver)
  const categoryRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Track last user-selected category to improve stability
  const lastSelectedCategory = useRef<string | null>(null);

  // Track timestamp of manual category selection
  const lastSelectedCategoryTimestamp = useRef<number>(0);

  // Simplified smooth scroll to category (no multi-phase approach needed)
  const handleScrollToCategory = (categoryName: string) => {
    // Record this as a manual selection
    lastSelectedCategory.current = categoryName;
    lastSelectedCategoryTimestamp.current = Date.now();

    setActiveCategory(categoryName);
    setIsScrollingToCategory(true);

    // If category data isn't loaded yet (unlikely after background loading)
    if (categoryLoadingStates[categoryName] === "not_loaded") {
      setCategoryLoadingStates((prev) => ({
        ...prev,
        [categoryName]: "loading",
      }));
      fetchCategoryProducts(categoryName);
    }

    // Simple, direct scroll to category without multi-phase adjustments
    requestAnimationFrame(() => {
      const categoryDiv = categoryRefs.current[categoryName];
      if (categoryDiv) {
        // Determine proper header height based on screen size
        const offset = window.innerWidth >= 768 ? 230 : 115;

        // Get the precise position from DOM since all content is fully loaded
        const categoryPosition =
          categoryDiv.getBoundingClientRect().top + window.pageYOffset - offset;

        // Smooth scroll directly to the category
        window.scrollTo({
          top: categoryPosition,
          behavior: "smooth",
        });

        // Reset scrolling state after animation completes
        setTimeout(() => {
          setIsScrollingToCategory(false);
        }, 500);
      }
    });
  };

  // IntersectionObserver to track which category is in view
  const observer = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(
      (entries) => {
        // Skip intersection handling during scrolling or loading
        if (isScrollingToCategory) return;

        // Find the most visible entry
        const intersectingEntries = entries.filter(
          (entry) => entry.isIntersecting,
        );
        if (intersectingEntries.length === 0) return;

        // Sort by visibility ratio to find the "most visible" category
        const mostVisibleEntry = intersectingEntries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev,
        );

        const catName = mostVisibleEntry.target.getAttribute("data-category");
        if (catName && catName !== activeCategory) {
          // If we have a recent manually selected category and it's starting to come into view,
          // prioritize staying on that category to avoid jumping
          if (
            lastSelectedCategory.current &&
            Date.now() - lastSelectedCategoryTimestamp.current < 3000 &&
            lastSelectedCategory.current === activeCategory
          ) {
            return;
          }

          setActiveCategory(catName);
        }
      },
      {
        rootMargin: "-40% 0px -40% 0px",
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5],
      },
    );

    // Attach the observer to each category div
    Object.entries(categoryRefs.current).forEach(([catName, ref]) => {
      if (ref && observer.current) {
        ref.setAttribute("data-category", catName);
        observer.current.observe(ref);
      }
    });

    return () => {
      if (observer.current) observer.current.disconnect();
    };
  }, [filteredCategoryProductsMap, isScrollingToCategory, activeCategory]);

  const handleSearchIconClick = () => {
    setSearchModalOpen(true);
    setTimeout(() => {
      mobileSearchInputRef.current?.focus();
    }, 100);
  };

  const handleCloseSearch = () => {
    setSearchModalOpen(false);
  };

  useEffect(() => {
    if (!isCategoriesLoading && branch && productsLoaded) {
      setMenuResourcesLoaded(true);
    }
  }, [isCategoriesLoading, branch, setMenuResourcesLoaded, productsLoaded]);

  // Improved renderProducts with better skeleton states
  const renderProducts = (catName: string) => {
    const products = filteredCategoryProductsMap[catName] || [];
    const loadingState = categoryLoadingStates[catName];
    const isActiveCategory = catName === activeCategory;

    // Always show skeleton for categories in "loading" state
    if (
      loadingState === "loading" ||
      (isActiveCategory && loadingState === "not_loaded")
    ) {
      const itemHeight = window.innerWidth >= 768 ? 240 : 112;
      const visibleItems = Math.ceil((windowHeight * 1.5) / itemHeight);

      return (
        <div className="md:flex md:justify-between md:flex-wrap md:w-full">
          {[...Array(Math.min(visibleItems, 8))].map((_, idx) => (
            <div key={idx} className="md:w-[48%] mb-4">
              <ProductCardSkeleton />
            </div>
          ))}
        </div>
      );
    }

    // For "not_loaded" state on non-active categories, show minimal skeleton
    if (loadingState === "not_loaded") {
      return (
        <div className="md:flex md:justify-between md:flex-wrap md:w-full">
          {[...Array(4)].map((_, idx) => (
            <div key={idx} className="md:w-[48%] mb-4">
              <ProductCardSkeleton />
            </div>
          ))}
        </div>
      );
    }

    // For "loaded" state, show actual products
    return (
      <div className="md:flex md:justify-between md:flex-wrap md:w-full">
        {products.length > 0 ? (
          products.map((product) => (
            <div key={product.id} className="md:w-[48%] mb-4">
              <Suspense fallback={<Spinner />}>
                <ProductCard product={product} />
              </Suspense>
            </div>
          ))
        ) : (
          // Empty state for categories with no products
          <div className="w-full py-4 text-center text-gray-500">
            No products available in this category
          </div>
        )}
      </div>
    );
  };

  //  If categories are STILL loading, show skeleton
  //  (Categories + a few "fake" product sections)
  if (isCategoriesLoading) {
    return (
      <div className="relative">
        <div className="sticky bg-white top-14 md:pt-6 md:pb-1 z-10">
          {/* Replace the entire category row with skeleton */}
          <CategorySkeleton />
        </div>

        {/* Show some "fake" product sections below the skeleton categories */}
        <div className="mx-5">
          {/* Example: Show a couple of category title placeholders + product skeletons */}
          {[...Array(2)].map((_, idx) => (
            <div key={idx} className="my-5">
              {/* Category title placeholder */}
              <div className="h-6 w-2/5 bg-gray-200 rounded-md animate-pulse mb-3" />
              {/* Product skeleton cards */}
              <div className="md:flex md:justify-between md:flex-wrap md:w-full">
                {[...Array(4)].map((__, idx2) => (
                  <div key={idx2} className="md:w-[48%] mb-4">
                    <ProductCardSkeleton />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Normal rendering once categories are loaded
  return (
    <div className="relative">
      {/* Sticky top for categories and search */}
      <div className="sticky bg-white top-14 md:pt-6 md:pb-1 z-10">
        <div className="flex justify-between items-center">
          <p className="text-2xl font-bold hidden md:block">
            All Offers from {branch.name}
          </p>
          <div className="hidden md:flex items-center justify-center mt-0 pt-2 relative">
            <div className="relative w-[21.5rem] flex items-center">
              <SearchIcon className="w-4 h-4 text-black absolute left-4 top-1/2 -translate-y-1/2 cursor-pointer" />
              <input
                type="text"
                placeholder="Search from menu..."
                className="w-full h-[2.92rem] pl-12 pr-4 rounded-[1.875rem] border text-black text-base focus:outline-none focus:ring-0"
                style={{
                  WebkitTapHighlightColor: "transparent",
                  outline: "none",
                  boxShadow: "none",
                  WebkitBoxShadow: "none",
                }}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
          </div>
        </div>

        {showProductTypeSwitches && (
          <div className="flex justify-start items-center gap-4 mt-4 md:mt-6">
            <button
              onClick={() => setProductType("Physical Product")}
              className={`px-4 py-2 rounded-full text-sm md:text-base font-medium transition-colors duration-300 ${
                productType === "Physical Product"
                  ? "bg-redCrimson text-white"
                  : "bg-gray-100 text-gray-600"
              }`}
            >
              Physical Products
            </button>
            <button
              onClick={() => setProductType("Service")}
              className={`px-4 py-2 rounded-full text-sm md:text-base font-medium transition-colors duration-300 ${
                productType === "Service"
                  ? "bg-redCrimson text-white"
                  : "bg-gray-100 text-gray-600"
              }`}
            >
              Services
            </button>
          </div>
        )}

        {/* Replace custom category navigation with our improved component */}
        <div className="relative w-full">
          <CategoryNavigation
            categories={sortedOpenCategories.map((cat) => cat.name)}
            selectedCategory={activeCategory}
            onCategorySelect={handleScrollToCategory}
            isCategoryFixed={false}
          />

          {/* Mobile search icon to open the overlay */}
          <div className="md:hidden absolute right-2 top-1/2 -translate-y-1/2 z-20 px-2 py-2">
            <button onClick={handleSearchIconClick}>
              <SearchIcon
                className="inline-block w-5 h-5 cursor-pointer"
                color="black"
              />
            </button>
          </div>
        </div>
      </div>

      {/* Display the current search text as a filter chip if present */}
      {searchText && (
        <div className="mt-2 mx-2">
          <p className="inline-flex items-center bg-red-200 rounded-full py-1 px-4">
            <span className="text-sm font-bold text-redCrimson">
              {searchText}
            </span>
            <button
              onClick={() => setSearchText("")}
              className="text-xs font-bold text-redCrimson ml-2"
            >
              X
            </button>
          </p>
        </div>
      )}

      {/* Daily Specials Section */}
      <div className="mx-5">
        <DailySpecialSection branchId={branch.id} />
      </div>

      {/* Render categories & products, in sorted order */}
      <div className="mx-5">
        {sortedOpenCategories.map((cat) => {
          const catName = cat.name;
          return (
            <div
              key={catName}
              ref={(el) => {
                categoryRefs.current[catName] = el;
              }}
              data-category={catName}
            >
              <p className="text-2xl font-bold my-5">{catName}</p>
              {renderProducts(catName)}
            </div>
          );
        })}
      </div>

      {/* The search overlay (mobile) */}
      {searchModalOpen && (
        <div className="fixed top-0 inset-0 z-[1000] flex flex-col bg-black bg-opacity-50">
          {/* White search bar at top */}
          <div className="bg-white h-14 flex items-center px-4">
            <input
              ref={mobileSearchInputRef}
              type="text"
              className="flex-1 h-10 border-0 bg-grayCloud rounded-full px-4"
              placeholder="Search from menu"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            <button
              className="ml-2 text-gray-600 font-bold"
              onClick={handleCloseSearch}
            >
              <CloseIcon className="w-4 h-4" />
            </button>
          </div>

          {/* The dark overlay below – clicking closes modal */}
          <div className="flex-1" onClick={handleCloseSearch}></div>
        </div>
      )}
    </div>
  );
};

export default Menu;
